import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Lock } from 'lucide-react'
import { useState } from 'react'

interface NavLinkProps {
  href: string
  icon?: any
  children: React.ReactNode
  isActive?: boolean
  locked?: boolean
  badge?: string | number
  className?: string
  external?: boolean
}

export function NavLink({
  href,
  icon: Icon,
  children,
  isActive = false,
  locked = false,
  badge,
  className,
  external = false
}: NavLinkProps) {
  const [isHovered, setIsHovered] = useState(false)

  const baseClasses = cn(
    'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative',
    'hover:bg-muted/50',
    {
      'bg-digestr-500/10 text-digestr-500 border border-digestr-500/20': isActive,
      'text-muted-foreground hover:text-foreground': !isActive && !locked,
      'text-muted-foreground/50 cursor-not-allowed': locked,
    },
    className
  )

  // Determine icon variant
  const iconVariant = isActive ? 'active' : isHovered ? 'hover' : 'default'

  const content = (
    <>
      {Icon && (
        <Icon
          className={cn('w-5 h-5', { 'opacity-50': locked })}
          variant={iconVariant}
        />
      )}
      <span className={cn({ 'opacity-50': locked })}>
        {children}
      </span>
      {locked && (
        <Lock className="w-3 h-3 opacity-50 ml-1 text-muted-foreground" />
      )}
      {badge && (
        <span className="absolute -top-1 -right-1 bg-accent-cyan-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
          {badge}
        </span>
      )}
    </>
  )

  if (locked) {
    return (
      <div className={baseClasses} title="Upgrade to access this feature">
        {content}
      </div>
    )
  }

  if (external) {
    return (
      <a
        href={href}
        className={baseClasses}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {content}
      </a>
    )
  }

  return (
    <Link
      to={href}
      className={baseClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {content}
    </Link>
  )
}